/**
 * ProductListRow - Linha compacta de produto
 * Exibe produto em formato de linha para lista
 *
 * Versão: 1.2.0
 * - Adicionadas colunas Unidade, Mensalidade e Prazo (suporte a serviços recorrentes)
 * - Mantido layout responsivo (info extra descem para segunda linha em mobile)
 */
import React from 'react';
import { Package, Wrench, Plus, Minus, RefreshCw } from 'lucide-react';
import { InlineEditableField } from './InlineEditableField';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/currency-input';
import { CrmProduct } from '@/hooks/useCrmProducts';
import {
  formatRecurrenceLabel,
  formatTermLabel,
  isRecurringService,
} from '@/lib/crm/recurringServiceUtils';

// Props do componente
interface ProductListRowProps {
  product: CrmProduct;
  priceTablePrice: number | null;
  isInDeal: boolean;
  dealQuantity: number;
  onAdd: (productId: string, quantity: number, unitPrice: number) => void;
  onUpdateQuantity: (productId: string, quantity: number) => void;
  onRemove: (productId: string) => void;
  currency: string;
  /**
   * Quando true e o produto é serviço recorrente, usa product.recurring_price
   * como preço unitário ao adicionar (em vez do default_price/preço da tabela).
   * Usado pelo modal Nova Recorrência (Financeiro).
   */
  useRecurringPrice?: boolean;
}


/**
 * Formata valor monetário
 */
function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency,
  }).format(value);
}

/**
 * Linha compacta de produto para lista
 */
export function ProductListRow({
  product,
  priceTablePrice,
  isInDeal,
  dealQuantity,
  onAdd,
  onUpdateQuantity,
  onRemove,
  currency,
  useRecurringPrice = false,
}: ProductListRowProps) {
  // Preço aberto (valor sob demanda): valor digitado na hora de adicionar
  const isOpenPrice = !!product.is_open_price;
  const [localPrice, setLocalPrice] = React.useState<number>(0);

  // Preço efetivo (tabela ou padrão)
  const effectivePrice = priceTablePrice ?? product.default_price;
  const hasTablePrice = priceTablePrice !== null;

  // Ícone baseado no tipo
  const TypeIcon = product.product_type === 'service' ? Wrench : Package;

  // Flag de serviço recorrente
  const recurring = isRecurringService(product.product_type, product.unit_type);

  // Labels formatados (somente para recorrente)
  const monthlyLabel = recurring && product.recurring_price
    ? `${formatCurrency(product.recurring_price, currency)} / ${formatRecurrenceLabel(product.recurrence_interval)}`
    : null;
  const termLabel = recurring
    ? formatTermLabel(product.term_type, product.term_quantity, product.term_unit)
    : null;

  // Preço de adição: para recorrentes em contexto de Nova Recorrência usamos
  // recurring_price (mensalidade); caso contrário mantém o comportamento clássico
  // (preço efetivo = tabela ou default_price).
  const addPrice = isOpenPrice
    ? localPrice
    : useRecurringPrice && recurring && product.recurring_price != null
      ? product.recurring_price
      : effectivePrice;

  // Handlers
  const handleAdd = () => {
    // Preço aberto exige valor informado maior que zero
    if (isOpenPrice && (!localPrice || localPrice <= 0)) return;
    onAdd(product.id, 1, addPrice);
    setLocalPrice(0);
  };
  const handleIncrement = () => onUpdateQuantity(product.id, dealQuantity + 1);
  const handleDecrement = () => {
    if (dealQuantity <= 1) onRemove(product.id);
    else onUpdateQuantity(product.id, dealQuantity - 1);
  };


  return (
    <div
      className={`
        flex items-center gap-2 md:gap-3 px-2 md:px-3 py-1.5 md:py-2 rounded-md border
        transition-all duration-150 hover:bg-muted/50
        ${isInDeal ? 'bg-primary/5 border-primary/20' : 'bg-background border-border/50'}
      `}
    >
      {/* Ícone e Nome (com info extras em mobile) */}
      <div className="flex items-center gap-1.5 md:gap-2 flex-1 min-w-0">
        <TypeIcon className="h-3.5 w-3.5 md:h-4 md:w-4 text-muted-foreground shrink-0" />
        <div className="min-w-0 flex-1">
          <p
            className="text-xs md:text-sm font-medium truncate"
            title={product.name}
          >
            {product.name}
          </p>
          {product.sku && (
            <p className="text-[10px] md:text-xs text-muted-foreground truncate">
              SKU: {product.sku}
            </p>
          )}
          {/* Mobile-only: mensalidade/prazo na segunda linha */}
          {recurring && (monthlyLabel || termLabel) && (
            <p className="text-[10px] text-primary truncate md:hidden flex items-center gap-1">
              <RefreshCw className="h-2.5 w-2.5 shrink-0" />
              {monthlyLabel}
              {termLabel ? ` · ${termLabel}` : ''}
            </p>
          )}
        </div>
      </div>

      {/* Unidade - oculta no mobile */}
      <Badge
        variant="outline"
        className="shrink-0 text-[10px] px-1.5 py-0 hidden md:inline-flex capitalize"
        title="Unidade"
      >
        {product.unit_type}
      </Badge>

      {/* Mensalidade - apenas desktop, somente recorrente */}
      <div className="shrink-0 text-right hidden md:block min-w-[110px]">
        {recurring && monthlyLabel ? (
          <p className="text-xs text-primary flex items-center justify-end gap-1">
            <RefreshCw className="h-3 w-3" />
            {monthlyLabel}
          </p>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </div>

      {/* Prazo - apenas desktop, somente recorrente */}
      <div className="shrink-0 text-right hidden md:block min-w-[110px]">
        {recurring && termLabel ? (
          <span className="text-xs text-foreground">{termLabel}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </div>

      {/* Categoria - oculta no mobile */}
      {product.category && (
        <Badge
          variant="secondary"
          className="shrink-0 text-xs px-2 py-0.5 max-w-[100px] truncate hidden lg:inline-flex"
          title={product.category.name}
        >
          {product.category.name}
        </Badge>
      )}

      {/* Preço */}
      <div className="shrink-0 text-right min-w-[70px] md:min-w-[90px]">
        {isOpenPrice ? (
          /* Preço aberto: input de valor manual */
          /* Máscara monetária BR (R$) para preço sob demanda */
          <CurrencyInput
            value={localPrice}
            onChange={(v) => setLocalPrice(Math.max(0, v))}
            currency={currency}
            className="h-6 md:h-7 w-[90px] md:w-[110px] text-xs"
          />
        ) : (
          <p className={`text-xs md:text-sm font-medium ${hasTablePrice ? 'text-primary' : ''}`}>
            {formatCurrency(effectivePrice, currency)}
          </p>
        )}
        {!isOpenPrice && hasTablePrice && product.default_price !== effectivePrice && (
          <p className="text-[10px] md:text-xs text-muted-foreground line-through">
            {formatCurrency(product.default_price, currency)}
          </p>
        )}
      </div>

      {/* Ações */}
      <div className="shrink-0 w-[80px] md:w-[100px] flex justify-end">
        {isInDeal ? (
          <div className="flex items-center gap-0.5 md:gap-1 bg-muted/50 rounded-md p-0.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 md:h-6 md:w-6"
              onClick={handleDecrement}
            >
              <Minus className="h-3 w-3" />
            </Button>
            <InlineEditableField
              value={dealQuantity}
              onChange={(newQty) => {
                if (newQty === 0) onRemove(product.id);
                else onUpdateQuantity(product.id, newQty);
              }}
              min={0}
              allowDecimals={false}
              className="w-8 md:w-10 h-5 md:h-6 text-center text-xs md:text-sm font-medium p-0"
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 md:h-6 md:w-6"
              onClick={handleIncrement}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="h-6 md:h-7 text-[10px] md:text-xs px-2 md:px-3"
            disabled={isOpenPrice && (!localPrice || localPrice <= 0)}
            onClick={handleAdd}
          >
            <Plus className="h-3 w-3 mr-0.5 md:mr-1" />
            <span className="hidden sm:inline">Adicionar</span>
            <span className="sm:hidden">Add</span>
          </Button>
        )}
      </div>
    </div>
  );
}
